import { IncomingHttpHeaders } from "http"
import type { ParsedUrlQuery } from "querystring"
import { NextApiRequest, NextApiResponse } from "next"
import { NextRequest, NextResponse } from "next/server"
import {
  AUTH_TOKEN_COOKIE,
  AuthV2Response,
  BASIC_AUTH_COOKIE,
  BASIC_AUTH_HEADER,
  BASIC_AUTH_QUERY_PARAM,
  OLD_AUTH_COOKIE,
  SESSION_PROPS_COOKIE,
} from "@/types/auth"
import { EmbeddedParamName } from "@/types/embedded"
import { getCookie, setCookie } from "cookies-next"
import { Address4, Address6 } from "ip-address"
import pino from "pino"

import { env } from "@/lib/env"
import { fetchWithRetry } from "@/lib/fetch-utils"
import { MAX_COOKIE_AGE_SECONDS } from "./lib/auth/cookies"

export function checkForBasicAuth(
  request: NextRequest,
  credentials?: { user: string; password: string }
): false | { userTester: boolean } {
  let authHeader = ""

  const cookie = getCookie(BASIC_AUTH_COOKIE, { req: request })
  const header = request.headers.get(BASIC_AUTH_HEADER)
  const queryParam = request.nextUrl.searchParams.get(BASIC_AUTH_QUERY_PARAM)

  if (cookie) {
    authHeader = cookie
  } else if (header) {
    authHeader = header
  } else if (queryParam) {
    authHeader = queryParam
  } else if (request.headers instanceof Headers) {
    authHeader = request.headers.get("authorization") || ""
  } else {
    authHeader = request.headers["authorization"] || ""
  }

  // If you get this far, we've decided you need credentials,
  // and if you don't have them we'll return a response that
  // makes the browser prompt for them.
  const authHeaderValue = authHeader
  if (authHeaderValue) {
    const encodedCredentials = authHeaderValue.replace(/^Basic /, "")
    if (encodedCredentials) {
      // base64 decode the credentials
      const [user, password] = atob(encodedCredentials).split(":")
      const expectedUser = credentials?.user ?? env("BASIC_AUTH_USER")
      const expectedPassword =
        credentials?.password ?? env("BASIC_AUTH_PASSWORD")
      if (user === expectedUser && password === expectedPassword) {
        return { userTester: false }
      } else if (
        !credentials &&
        user === env("TESTER_BASIC_AUTH_USER") &&
        password === env("TESTER_BASIC_AUTH_PASSWORD")
      ) {
        return { userTester: true }
      }
    }
  }
  return false
}

export function getConnectingIp(req: NextRequest): string {
  const xForwardedFor = req.headers.get("x-forwarded-for")
  if (xForwardedFor) {
    // the last IP is the connecting IP of the CF proxy
    const xForwardedIps = xForwardedFor.split(",")
    return xForwardedIps[xForwardedIps.length - 1].trim()
  }
  // req.ip was removed in Next 16 (it was Vercel-only and always undefined on
  // our Heroku/Cloudflare stack). x-forwarded-for is the only reliable source.
  return ""
}

// checks if the connecting IP is in a list of cloudflare trusted proxies
// as no other IPs should be connecting, ensure you send the IP that is connecting to the FE and
// NOT the IP of the customer's browser as that one for sure will not be trusted
export function trustedIp(
  connectingIp: string,
  trustedProxies: string[] | undefined,
  logger: pino.Logger
): boolean {
  if (connectingIp === "") return false

  const appName = env("HEROKU_APP_NAME") || env("RENDER_SERVICE_NAME")
  if (!appName?.match(/-prod|^prod-/)) return true

  if (!trustedProxies) return false

  // much easier if we were using the ip library but that one has a critical vulnerability
  // # npm audit report
  //
  // ip  *
  // Severity: high
  // ip SSRF improper categorization in isPublic - https://github.com/advisories/GHSA-2p57-rm9w-gvfp
  // No fix available
  // node_modules/ip
  //
  // 1 high severity vulnerability
  // return trustedProxies.some(proxy => ip.cidrSubnet(proxy).contains(connectingIp))
  return trustedProxies.some((proxy) => {
    try {
      if (Address4.isValid(proxy)) {
        if (Address4.isValid(connectingIp)) {
          // proxy is IPv4 and connecting IP is IPv4
          const proxySubnet = new Address4(proxy)
          const ipInQuestion = new Address4(connectingIp)
          return ipInQuestion.isInSubnet(proxySubnet)
          // return subnet.isInSubnet(ipInQuestion)
        } else if (Address6.isValid(connectingIp)) {
          logger.info(
            `proxy is IPv4 ${proxy} and connecting IP is IPv6 ${connectingIp}`
          )
          const proxySubnet = new Address4(proxy)
          const ipInQuestion = new Address6(connectingIp)
          return ipInQuestion.isInSubnet(proxySubnet)
        }
      } else if (Address6.isValid(proxy)) {
        if (Address6.isValid(connectingIp)) {
          // proxy is IPv6 and connecting IP is IPv6
          const proxySubnet = new Address6(proxy)
          const ipInQuestion = new Address6(connectingIp)
          return ipInQuestion.isInSubnet(proxySubnet)
        } else if (Address4.isValid(connectingIp)) {
          logger.info(
            `proxy is IPv6 ${proxy} and connecting IP is IPv4 ${connectingIp}`
          )
          const proxySubnet = new Address6(proxy)
          const ipInQuestion = new Address4(connectingIp)
          return ipInQuestion.isInSubnet(proxySubnet)
        }
      }
    } catch (e) {
      logger.error(`Error parsing IP address: ${e}`)
      return false
    }
    return false
  })
}

export function cfVerifiedBot(headers: Headers | IncomingHttpHeaders) {
  // this header is always set by CF via one of our transform rules as long as the
  // original request goes through CF, a bad actor can not overwrite this header
  // to pretend to be a CF known bot, this is different than the CF verified bot
  // which is a paid feature on their enterprise plan
  const value =
    headers instanceof Headers
      ? headers.get("mt-cf-client-bot")
      : headers["mt-cf-client-bot"]
  return value === "true"
}

export const ALLOWED_SEO_BOTS =
  /.*(?:Googlebot|AdsBot-Google|GoogleOther|bingbot|Applebot|Yahoo! Slurp|Google-InspectionTool|facebookexternalhit|DuckDuckBot|YandexBot|OAI-SearchBot|ChatGPT-User|PerplexityBot|Perplexity-User|Claude-SearchBot|Claude-User).*/

export const ALLOWED_LINK_PREVIEW_BOTS_KNOWN_TO_CF =
  /^(Slackbot|TelegramBot|ZoominfoBot|Iframely|.*(SkypeUriPreview|Twitterbot|Slack-ImgProxy).*)/

export const ALLOWED_LINK_PREVIEW_BOTS_UNKNOWN_TO_CF =
  /^(WhatsApp|.*(Discordbot|facebookexternalhit).*)/

// in question:
// "Mozilla/5.0 (compatible; SemrushBot; +http://www.semrush.com/bot.html)
// "Iframely/1.3.1 (+https://iframely.com/docs/about)"
// "Mozilla/5.0 (compatible; AhrefsBot/7.0; +http://ahrefs.com/robot/)"
// "Mozilla/5.0 (iPhone; CPU iPhone OS 6_0 like Mac OS X) AppleWebKit/536.26 (KHTML, like Gecko) Version/6.0 Mobile/10A5376e Safari/8536.25 (compatible; SiteAuditBot/0.97; +http://www.semrush.com/bot.html"

export function isAllowedSeoBot(headers: Headers) {
  return (
    cfVerifiedBot(headers) &&
    headers.get("user-agent")?.match(ALLOWED_SEO_BOTS) != null
  )
}

// Mirrors DeviceDetector's generic catch-all pattern — any UA containing bot/crawler/spider
// is treated as a crawler regardless of CF verification, matching backend behavior.
const GENERIC_BOT_UA_PATTERN = /(?:bot|crawler|spider)(?:[^a-z]|$)/i

export function isCrawler(headers: Headers) {
  if (
    // eslint-disable-next-line no-process-env
    process.env.NODE_ENV === "development" &&
    headers.get("mt-use-crawler") === "1"
  ) {
    // In development mode, we allow a special header to indicate a crawler
    // This is useful for testing purposes
    return true
  }

  const knownAndAllowed =
    isAllowedSeoBot(headers) ||
    (cfVerifiedBot(headers) &&
      headers.get("user-agent")?.match(ALLOWED_LINK_PREVIEW_BOTS_KNOWN_TO_CF) !=
        null)
  const allowedUnknownToCf =
    headers.get("user-agent")?.match(ALLOWED_LINK_PREVIEW_BOTS_UNKNOWN_TO_CF) !=
    null
  const genericBot = GENERIC_BOT_UA_PATTERN.test(
    headers.get("user-agent") ?? ""
  )

  return knownAndAllowed || allowedUnknownToCf || genericBot
}

export function createServerCrawlerAuthCookie(
  req: NextRequest,
  res: NextResponse,
  data: Pick<AuthV2Response, "auth_token">
) {
  const expires = edgeAddSecondsToDate(
    edgeCreateDate(),
    data.auth_token.expires_in
  )

  const authDomain = env("NEXT_COOKIE_AUTH_DOMAIN")
  if (!authDomain) {
    throw new Error("NEXT_COOKIE_AUTH_DOMAIN is not set.")
  }

  setCookie("mindtrip.auth_token", data.auth_token.value, {
    req,
    res,
    path: "/",
    httpOnly: true,
    expires: expires,
    sameSite: "lax",
    secure: needsSSL(),
    domain: authDomain,
  })
}

export interface SessionProps {
  utmParams?: Record<string, string>
  referrer?: string
  amplitudeSessionId?: number
}

export function createSessionPropsCookie(
  req: NextRequest,
  res: NextResponse,
  data: SessionProps
) {
  const existingCookieStr = req.cookies.get(SESSION_PROPS_COOKIE)?.value
  let existingData: SessionProps = {}

  if (existingCookieStr) {
    try {
      existingData = JSON.parse(existingCookieStr) as SessionProps
    } catch (e) {
      // Invalid cookie format, proceed with creating a new one
    }
  }

  const newData: SessionProps = {
    ...existingData, // Preserve existing amplitudeSessionId
    utmParams: data.utmParams || {},
    referrer: data.referrer || undefined,
  }

  const hasChanged =
    JSON.stringify(newData.utmParams) !==
      JSON.stringify(existingData.utmParams || {}) ||
    newData.referrer !== existingData.referrer

  // Only set cookie if:
  // 1. There's actual data to store AND
  // 2. Either no existing cookie OR data has changed
  if (
    (newData.referrer || Object.keys(newData.utmParams || {}).length > 0) &&
    (!existingCookieStr || hasChanged)
  ) {
    const expires = edgeAddSecondsToDate(edgeCreateDate(), 30 * 60)

    const cookieAuthDomain = env("NEXT_COOKIE_AUTH_DOMAIN")
    if (!cookieAuthDomain) {
      throw new Error("NEXT_COOKIE_AUTH_DOMAIN is not set.")
    }

    const authDomain = cookieAuthDomain

    setCookie(SESSION_PROPS_COOKIE, JSON.stringify(newData), {
      req,
      res,
      path: "/",
      httpOnly: false,
      expires: expires,
      sameSite: "lax",
      secure: needsSSL(),
      domain: authDomain,
    })
  }
}

/**
 * determines if a referrer url is external to mindtrip.ai domain
 * @param referrer - The referrer url to check
 * @returns true if external, false if internal or invalid
 */
export function isExternalReferrer(referrer: string): boolean {
  if (!referrer) return false

  const match = referrer.match(/^https?:\/\/([^/]+)/i)
  if (!match) return false // Invalid URL format

  const hostname = match[1].toLowerCase()
  const internalDomainPattern = /^(www\.)?(.+\.)?mindtrip\.ai$/

  return !internalDomainPattern.test(hostname)
}

export function isSearchEngineReferrer(referrer?: string): boolean {
  if (!referrer) return false

  try {
    const cleanedReferrer = referrer.trim().replace(/^"|"$/g, "")
    const hostname = new URL(cleanedReferrer).hostname.toLowerCase()
    const googleHostnamePattern =
      /^(?:www\.)?google\.(?:[a-z]{2,3}|(?:co|com)\.[a-z]{2})$/

    return (
      googleHostnamePattern.test(hostname) ||
      hostname === "bing.com" ||
      hostname.endsWith(".bing.com") ||
      hostname === "duckduckgo.com" ||
      hostname.endsWith(".duckduckgo.com") ||
      hostname === "search.yahoo.com"
    )
  } catch {
    return false
  }
}

export function createBasicAuthCookie(
  req: NextApiRequest,
  res: NextApiResponse,
  data: {
    username: string
    password: string
  }
) {
  const cookieAuthDomain = env("NEXT_COOKIE_AUTH_DOMAIN")
  if (!cookieAuthDomain) {
    throw new Error("NEXT_COOKIE_AUTH_DOMAIN is not set.")
  }

  const authDomain = cookieAuthDomain

  const expires = edgeAddSecondsToDate(
    edgeCreateDate(),
    MAX_COOKIE_AGE_SECONDS - 10 // 10 second buffer
  )

  const value = btoa(data.username + ":" + data.password)

  setCookie(BASIC_AUTH_COOKIE, value, {
    req,
    res,
    path: "/",
    httpOnly: true,
    expires: expires,
    sameSite: "lax",
    secure: needsSSL(),
    domain: authDomain,
  })
}

export function needsSSL() {
  return (
    (env("NODE_ENV") === "production" && env("LOCAL_PROD") !== "true") ||
    env("FORCE_SSL") === "true"
  )
}

export function tokenString(apiServerToken: string) {
  return JSON.stringify({ apiServerToken })
}

// todo needed for /api/events. need to find a better way to do that.
export function getOldAuthParam(params: URLSearchParams | ParsedUrlQuery) {
  return getEmbeddedParam(params, OLD_AUTH_COOKIE)
}

export function getAuthParam(params: URLSearchParams | ParsedUrlQuery) {
  return getEmbeddedParam(params, AUTH_TOKEN_COOKIE)
}

export function getEmbeddedParam(
  params: URLSearchParams | ParsedUrlQuery,
  name: EmbeddedParamName
) {
  if (params instanceof URLSearchParams) {
    return params.get(name)
  } else {
    return params[name] as string
  }
}

// keep in sync with NEXT_PUBLIC_MICROSITE_ORGIDS in env.local (and env.ts allowlist)
const MICROSITE_ORGIDS = new Set(
  (env("NEXT_PUBLIC_MICROSITE_ORGIDS") ?? "")
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean)
)

// Returns the orgid for a microsite request (e.g. visitcalifornia.mindtrip.ai),
// derived from the host's subdomain, or undefined for non-microsite hosts.
export function micrositeOrgidForHost(
  host: string | undefined | null
): string | undefined {
  const subdomain = (host ?? "").split(".")[0]
  return MICROSITE_ORGIDS.has(subdomain) ? subdomain : undefined
}

export function getOrgParams(
  params: URLSearchParams | ParsedUrlQuery
): Array<[key: string, value: string]> {
  const res: Array<[key: string, value: string]> = []

  ;["orgid", "orgurl", "geoname"].forEach((key) => {
    let value: string | null = null
    if (params instanceof URLSearchParams) {
      value = params.get(key)
    } else {
      value = typeof params[key] === "string" ? params[key] : null
    }

    if (value) {
      res.push([key, value])
    }
  })
  return res
}

export const FORWARD_HEADER_PREFIX = "mt-forwarded-"

// forwards the original headers, filters out some like cookie, authorization,
// let those more sensitive headers be an explicit forward from the caller
export function forwardNativeHeaders(
  incomingHeaders: Headers,
  outgoingHeaders: Headers
) {
  const disallowedHeaders = ["cookie", "authorization"]
  for (const key of incomingHeaders.keys()) {
    const lowercaseKey = key.toLowerCase()
    if (!disallowedHeaders.includes(lowercaseKey)) {
      if (!lowercaseKey.startsWith(FORWARD_HEADER_PREFIX)) {
        const value = incomingHeaders.get(key)
        if (value) {
          outgoingHeaders.append(FORWARD_HEADER_PREFIX + lowercaseKey, value)
        }
      }
    }
  }
}

export async function createCrawlerAuthToken(
  request: NextRequest
): Promise<Pick<AuthV2Response, "auth_token">> {
  const headers = {
    Accept: "application/json",
    "Content-Type": "application/json",
    cookies: request.headers.get("cookie") || "",
    "X-Mindtrip-AbusePrevention": env("ABUSE_PREVENTION_SECRET") || "",
    Authorization: "Bearer " + env("API_SERVER_SECRET"),
  }
  const outGoingHeaders = new Headers(headers)

  forwardNativeHeaders(request.headers, outGoingHeaders)

  const response = await fetchWithRetry(
    fetch,
    new URL("/api/auth/crawler", env("API_SERVER_URL")),
    {
      method: "POST",
      headers: outGoingHeaders,
    },
    { retries: 2 }
  )

  return await response.json()
}

export function mapIncomingHeadersToWebHeaders(
  incomingHeaders: IncomingHttpHeaders
): Headers {
  const headers = new Headers()

  for (const [key, value] of Object.entries(incomingHeaders)) {
    if (value !== undefined) {
      if (Array.isArray(value)) {
        value.forEach((v) => headers.append(key, v))
      } else {
        headers.set(key, value)
      }
    }
  }

  return headers
}

/**
 * Date utilities for proxy-utils
 * These use Date directly (bypassing the no-restricted-globals rule)
 * because date-utils has dependencies that shouldn't be pulled into the proxy
 */

/* eslint-disable no-restricted-globals -- Date operations are allowed in these proxy-specific utilities */

/**
 * Get current timestamp in milliseconds
 */
export function edgeNowMillis(): number {
  return Date.now()
}

/**
 * Create a new Date object
 */
export function edgeCreateDate(timestamp?: number): Date {
  return timestamp ? new Date(timestamp) : new Date()
}

/**
 * Add seconds to a date and return a new Date
 */
export function edgeAddSecondsToDate(date: Date, seconds: number): Date {
  return new Date(date.getTime() + seconds * 1000)
}

/**
 * Add days to a date and return a new Date
 */
export function edgeAddDaysToDate(date: Date, days: number): Date {
  return new Date(date.getTime() + days * 24 * 60 * 60 * 1000)
}

/**
 * Calculate seconds between two timestamps
 */
export function edgeSecondsBetween(startMs: number, endMs: number): number {
  return Math.floor((endMs - startMs) / 1000)
}

/* eslint-enable no-restricted-globals -- Date */

export async function pendingMigrationCheck(
  pathname: string
): Promise<boolean> {
  try {
    if (pathname.startsWith("/_next")) {
      return false
    }
    const response = await fetch("http://localhost:3000/ping")
    const responseText = await response.text()
    console.log(
      "ping check",
      response.status,
      responseText.includes("ActiveRecord::PendingMigrationError")
    )
    return (
      response.status === 500 &&
      responseText.includes("ActiveRecord::PendingMigrationError")
    )
  } catch {
    return false
  }
}
